import { PageHeading } from '@quad/bootstrap-react';
import { Badge, Col, Container, Row } from 'react-bootstrap';
import ScrollPageContent from '../../areas/shared/ScrollPageContent';
import AppManagerClient from '../../api/AppManagerClient';
import { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { IApplication } from '../../api/models';
import { NavBar, NavBarItem } from '../Shared/NavBar';
import ApplicationsTable from './ApplicationsTable';
import { AppType } from '../../constants/AppType';
import ApplicationsCreate from './ApplicationsCreate';
import { useAppMakeSelector } from '../../hooks/useAppSelector';
import userPrincipalSelectors from '../../store/userPrincipal/selectors';
import { Permission as PermissionString } from '../../permissions/permission';
import { ApplicationTableColumns } from '../../constants/ApplicationTableItems';

interface ApplicationsProps {
    type?: string;
    title: string;
    icon: string;
}
function Applications({ type, title, icon }: ApplicationsProps) {
    const hiddenColumns: [columnNames: ApplicationTableColumns] = [ApplicationTableColumns.Type];

    const [allApplications, setAllApplications] = useState(Array<IApplication>());
    const [allAppsAreLoading, setAllAppsAreLoading] = useState(true);
    const [userApplications, setUserApplications] = useState(Array<IApplication>());
    const [userAppsAreLoading, setUserAppsAreLoading] = useState(true);

    const loadAllApps = useCallback(async () => {
        try {
            const apps = type ? await AppManagerClient.getApplicationsByType(type) : await AppManagerClient.getAllApplications();
            setAllApplications(apps);
        } finally {
            setAllAppsAreLoading(false);
        }
    }, [type]);

    const loadUserApps = useCallback(async () => {
        try {
            const apps = type ? await AppManagerClient.getUserApplicationsByType(type) : await AppManagerClient.getUserApplications();
            setUserApplications(apps);
        } finally {
            setUserAppsAreLoading(false);
        }
    }, [type]);

    useEffect(() => {
        loadAllApps();
        loadUserApps();
    }, [loadAllApps, loadUserApps]);

    const deleteApps = (apps: IApplication[], appName: string, setter: (apps: IApplication[]) => void) => {
        const shouldStay = _.filter(apps, (item: IApplication) => {
            return item.name !== appName;
        });
        setter(shouldStay);
    };
    const deleteAppHandler = (appName: string) => {
        deleteApps(allApplications, appName, setAllApplications);
        deleteApps(userApplications, appName, setUserApplications);
    };

    const hasCreateApplicationPermission = useAppMakeSelector(userPrincipalSelectors.makeSelectHasPermission, PermissionString.Root_Applications);
    const isWindowsService = type === AppType.WindowsService;
    const navBarsClass = isWindowsService ? 'col-11' : 'col-12';
    return (
        <ScrollPageContent>
            <PageHeading>
                <div className="col">
                    <h1>
                        <i className={icon}></i> {title}
                    </h1>
                </div>
            </PageHeading>

            <Container>
                <Row>
                    <Col className={navBarsClass}>
                        <NavBar>
                            <NavBarItem target="user-apps" active={true}>
                                <i className="fa fa-star"></i>
                                <span> My Applications </span>
                                <Badge className="row-count">{userApplications.length}</Badge>
                            </NavBarItem>
                            <NavBarItem target="all-apps">
                                <span> All Applications </span>
                                <Badge className="row-count">{allApplications.length}</Badge>
                            </NavBarItem>
                        </NavBar>
                    </Col>
                    {isWindowsService ? (
                        <Col className="col-1 center">
                            <Container>
                                <ApplicationsCreate spining={false} disabled={!hasCreateApplicationPermission} applicationType={type} />
                            </Container>
                        </Col>
                    ) : (
                        <></>
                    )}
                </Row>

                <Row>
                    <Col>
                        <div className="tab-content" id="pills-tabContent">
                            <div className="tab-pane fade show active container" id="user-apps" role="tabpanel" aria-labelledby="user-apps-tab">
                                <ApplicationsTable isLoading={userAppsAreLoading} data={userApplications} deleteAppHandler={deleteAppHandler} hiddenColumns={hiddenColumns} />
                            </div>
                            <div className="tab-pane fade container" id="all-apps" role="tabpanel" aria-labelledby="all-apps-tab">
                                <ApplicationsTable isLoading={allAppsAreLoading} data={allApplications} deleteAppHandler={deleteAppHandler} hiddenColumns={hiddenColumns} />
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </ScrollPageContent>
    );
}

export default Applications;
